import './DocumentModal.scss';
import '@organisms/document/DocumentWorkspace/DocumentWorkspace.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import Button from '@srm/ui/button';
import Modal from '@srm/ui/modal';
import { SubmitErrorHandler, useForm } from 'react-hook-form';
import { IDocumentData } from '@models/document/IDocumentData';
import { ConfigService } from '@/configuration/services/configService';
import { isDocumentResolverValidationEnabled } from '@utils/validation/document';
import {
    useDocumentResolver,
    useCentralizedDocumentInit,
} from '@organisms/document/useDocumentResolver';
import {
    CentralizedSideEffectsContext,
    CentralizedInitCompleteContext,
    CentralizedInitExecutedKeysContext,
    DocumentImperativeValidatorsContext,
    DocumentModalContext,
    DocumentValidationIndexContext,
} from '@/context/Contexts';
import { useFieldSaver } from '@molecules/formbuilder/hooks';
import Skeleton from '@srm/ui/skeleton';
import { sendModalMsg } from '@srm/ui/lib/modalMessages';
import { showSaveNotifications } from '@utils/saveNotifications';
import DocumentTabBuilder from '@organisms/document/DocumentTabBuilder/DocumentTabBuilder';
import { useFirstInvalidTabRequest } from '@organisms/document/DocumentTabBuilder/useFirstInvalidTabRequest';
import { IFieldElem } from '@models/IFormData';
import { getValueString } from '@utils/documentUtils';
import { useTypedSelector } from '@hooks/useTypedSelector';
import PageWrapper from '@srm/ui/page-wrapper';
import { v4 as uuidv4 } from 'uuid';
import DocumentModalTitle from '@organisms/DocumentModal/DocumentModalTitle/DocumentModalTitle';
import { IDocumentScheme } from '@models/document/IDocumentScheme';
import { ValidatorManager } from '@utils/managers/ValidatorManager';
import { NotificationsManager } from '@utils/managers/NotificationsManager';
import { ModalSize } from '@srm/ui/types/ModalSize';
import BackdropLoader from '@srm/ui/backdrop-loader';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@srm/ui/controls/HookFormProvider';
import { docInvalidFormHandler } from '@utils/helpers';
import { FormulaManager } from '@utils/managers/FormulaManager';
import DocumentFormController from '@controls/DocumentFormController/DocumentFormController';
import { useAnalytics } from '@/analytics';
import { useDocumentModalFields } from './useDocumentModalFields';
import DocumentModalSkeleton from './DocumentModalSkeleton/DocumentModalSkeleton';
import { useChangeRuntime } from '@controls/ChangeHandlers/useChangeHandlers';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';

export interface IDocumentModalProps {
    /** Схема документа */
    scheme?: IDocumentScheme;
    /** Данные документа */
    data?: IDocumentData;
    /** Загрузка */
    isLoading: boolean;
    /** Режим редактирования */
    isEdit: boolean;
    /** Режим создания нового документа */
    isNew: boolean;
    /** Сохранение документа */
    onSave: (data: IDocumentData) => Promise<void>;
    /** Сохранение с последующим открытием документа */
    onSaveAndOpen?: (data: IDocumentData) => Promise<void>;
    /** Закрытие окна */
    onCancel: () => void;
    /** Размер окна */
    modalSize?: ModalSize;
    /** Текст кнопки Сохранить */
    saveButtonText?: string;
    saveAndOpenButtonText?: string;
    /** Высота во весь экран */
    fullHeight?: boolean;
    /**
     * Запретить закрытие окна, пока сохраняет вызывающий: сохранение, начатое из сообщения формы
     * (уведомления перед сохранением), идёт уже после сабмита формы.
     */
    closeDisabled?: boolean;
}

const EMPTY_FIELDS: Record<string, IFieldElem> = {};

const DocumentModal = ({
    scheme,
    data,
    isLoading,
    isEdit,
    isNew,
    onSave,
    onSaveAndOpen,
    onCancel,
    modalSize = 'max',
    saveButtonText = 'Сохранить',
    saveAndOpenButtonText = 'Сохранить и перейти',
    fullHeight = true,
    closeDisabled: closeDisabledProp = false,
}: IDocumentModalProps) => {
    const changeRuntime = useChangeRuntime(scheme?.form?.changeActions);
    const [documentTitle, setDocumentTitle] = useState<string>();
    const submitAction = useRef<'save' | 'open'>('save');
    const [idForm, setIdForm] = useState(() => {
        return uuidv4();
    });

    const auth = useTypedSelector((state) => state.auth);
    const { analytics } = useAnalytics();
    const config = ConfigService.get();
    const fieldsData = useDocumentModalFields({ data, scheme, user: auth.user, isEdit, isNew });

    // --- Resolver-валидация (единый глобальный флаг для всех потоков). ---
    const useResolverValidation = isDocumentResolverValidationEnabled(config.application);
    const {
        resolver,
        validationIndex,
        setResolverContext,
        onSubmitValidationStart,
        onSubmitValidationEnd,
        imperativeValidators,
    } = useDocumentResolver({
            enabled: useResolverValidation,
            scheme,
            fields: fieldsData,
            analytics,
            telemetryEnabled: !!config.application.resolverValidation?.telemetryEnabled,
        });

    const methods = useForm<IDocumentData>({
        mode: 'onBlur', // "onChange"
        shouldUnregister: false,
        ...(resolver ? { resolver } : {}),
    });

    // Контекст resolver обновляется каждый рендер — resolver читает его лениво через ref-getter.
    // Debug-панель в модалке недоступна, поэтому debug-флаги не передаём.
    setResolverContext({
        isEdit,
        isNew,
        requestCacheScope: `${data?.id ?? 'new'}:${auth.user.id ?? ''}`,
    });
    const hasUnsavedInput = useUnsavedFormInput(methods);
    const closeDisabled = closeDisabledProp || methods.formState.isSubmitting;
    const onSaveField = useFieldSaver(fieldsData ?? EMPTY_FIELDS, methods);
    const handleModalError = useCallback((errors?: string[]) => {
        if (errors) sendModalMsg({ message: errors, type: 'error' });
    }, []);

    const { errorTabRequest, requestFirstInvalidTab, completeFirstInvalidTabRequest } =
        useFirstInvalidTabRequest();

    const getDocumentTitle = useCallback(() => {
        if (scheme?.isTemplate) {
            setDocumentTitle('Шаблон "' + scheme.templateName + '" (' + scheme.flowTitle + ')');
            return;
        }

        if (fieldsData && fieldsData['variables'] && fieldsData['variables'].value['caption']) {
            setDocumentTitle(fieldsData['variables'].value['caption']);
        } else {
            setDocumentTitle(
                getValueString('|Document|Имя_потока', fieldsData) +
                    ' ' +
                    getValueString('|Document|Регистрационный_номер', fieldsData),
            );
        }
    }, [fieldsData, scheme]);

    useEffect(() => {
        if (data && scheme) {
            setIdForm(uuidv4());
            methods.reset(data);
        }
    }, [data, methods, scheme]);

    useEffect(() => {
        FormulaManager.ConfigureFactsFromView(scheme?.form?.view);

        return () => {
            FormulaManager.ConfigureFactsFromView(undefined);
        };
    }, [scheme]);

    useEffect(() => {
        if (fieldsData && scheme) {
            getDocumentTitle();
        }
    }, [fieldsData, getDocumentTitle, scheme]);

    const { initCompleteStore: centralizedInitCompleteStore, executedKeys: centralizedExecutedKeys } = useCentralizedDocumentInit({
        enabled: useResolverValidation,
        ready: true,
        scheme,
        fields: fieldsData,
        formMethods: methods,
        isEdit,
        isNew,
        turnOff: false,
        onSaveField,
        changeRuntime,
        setError: handleModalError,
        initSignal: fieldsData,
        cancelSignal: data,
    });

    const submitData = async (data: IDocumentData, save: (data: IDocumentData) => Promise<void>) => {
        const errors: string[] = [];
        const notify: string[] = [];
        const validatorManager = new ValidatorManager(fieldsData!, methods);
        const notificationsManager = new NotificationsManager(fieldsData!, methods);
        await validatorManager.validateAllAsync(scheme?.form.view.validators?.validator, errors);
        if (errors.length === 0) {
            await notificationsManager.notificationsAllAsync(scheme?.form.view.notifications?.messages, notify);

            if (notify.length > 0) {
                showSaveNotifications(scheme?.form.view.notifications, notify, {
                    save: () => void save(data),
                    cancel: onCancel,
                });
            } else {
                await save(data);
            }
        } else {
            sendModalMsg({
                message: errors,
                type: 'error',
            });
        }
    };

    const onSubmit = async (data: IDocumentData) => {
        const save = submitAction.current === 'open' && onSaveAndOpen ? onSaveAndOpen : onSave;
        submitAction.current = 'save';
        await submitData(data, save);
    };

    const isPending = isLoading || !fieldsData || !data || !scheme;
    const modalHeader = isPending ? (
        <Skeleton height={24} width="min(280px, 55vw)" radius="sm" aria-hidden="true" />
    ) : (
        <DocumentModalTitle titleText={documentTitle} fieldsData={fieldsData} />
    );

    const modalFooter = (
        <div className={clsx('document-modal__footer')}>
            {(isNew || isEdit) && (
                <Button
                    variant="primary"
                    className="doc-save-btn"
                    data-testid="document-save-button"
                    size="sm"
                    type="submit"
                    form={idForm}
                    aria-label="Сохранить"
                    disabled={isPending || methods.formState.isSubmitting}
                    onClick={() => {
                        submitAction.current = 'save';
                    }}
                >
                    {saveButtonText}
                </Button>
            )}
            {(isNew || isEdit) && onSaveAndOpen && (
                <Button
                    variant="secondary"
                    size="sm"
                    type="submit"
                    form={idForm}
                    disabled={isPending || methods.formState.isSubmitting}
                    onClick={() => {
                        submitAction.current = 'open';
                    }}
                >
                    {saveAndOpenButtonText}
                </Button>
            )}
        </div>
    );

    const onInvalid: SubmitErrorHandler<IDocumentData> = (errors) => {
        submitAction.current = 'save';
        fieldsData && docInvalidFormHandler(errors, fieldsData);
        requestFirstInvalidTab(errors);
    };

    return (
        <Modal
            className={clsx('document-modal', fullHeight && 'document-modal--full-height')}
            size={modalSize}
            // Закрытие — отмена: во время сохранения его нет, правки пользователя теряются только после вопроса.
            onClose={onCancel}
            hasUnsavedInput={hasUnsavedInput}
            closeDisabled={closeDisabled}
            header={modalHeader}
            footer={modalFooter}
        >
            {isPending ? (
                <DocumentModalSkeleton />
            ) : (
                <CentralizedSideEffectsContext value={useResolverValidation}>
                  <CentralizedInitCompleteContext value={centralizedInitCompleteStore}>
                   <CentralizedInitExecutedKeysContext value={centralizedExecutedKeys}>
                    <DocumentImperativeValidatorsContext value={imperativeValidators}>
                    <DocumentValidationIndexContext value={validationIndex}>
                        <FormProviderControl
                            formMethods={methods}
                            className="document-modal__form"
                            id={idForm}
                            onSubmit={onSubmit}
                            onInvalid={onInvalid}
                            onSubmitValidationStart={onSubmitValidationStart}
                            onSubmitValidationEnd={onSubmitValidationEnd}
                            changeRuntime={changeRuntime}
                        >
                            <HookFormProvider controller={DocumentFormController}>
                                <PageWrapper className="document-modal__wrapper document-workspace">
                                    <DocumentModalContext value={{ data: data, scheme: scheme }}>
                                        <DocumentTabBuilder
                                            docId={data.id}
                                            baseDocId={data.baseDocKey!}
                                            scheme={scheme}
                                            methods={methods}
                                            setError={(errors?: string[]) => {
                                                if (errors) {
                                                    sendModalMsg({
                                                        message: errors,
                                                        type: 'error',
                                                    });
                                                }
                                            }}
                                            fields={fieldsData}
                                            isNew={isNew}
                                            isEdit={isEdit}
                                            turnOffInvokeChangesOnNew={false}
                                            useLinks={false}
                                            hideRoutes={true}
                                            hideStatusBar={true}
                                            hideRoutesMap={true}
                                            hideRouteApprove={true}
                                            hideEisLog={true}
                                            errorTabRequest={errorTabRequest}
                                            onErrorTabRequestHandled={completeFirstInvalidTabRequest}
                                        />
                                    </DocumentModalContext>
                                </PageWrapper>
                                {methods.formState.isSubmitting && <BackdropLoader label="Сохранение" />}
                            </HookFormProvider>
                        </FormProviderControl>
                    </DocumentValidationIndexContext>
                    </DocumentImperativeValidatorsContext>
                   </CentralizedInitExecutedKeysContext>
                  </CentralizedInitCompleteContext>
                </CentralizedSideEffectsContext>
            )}
        </Modal>
    );
};

export default DocumentModal;
